import type { ScanResult, ScanEvent } from '../types';

// Public scanner API: cross-origin in production (api.protbyte.com).
const BASE = import.meta.env.VITE_API_BASE ?? '';

// Admin API: SAME-ORIGIN with the page (sentinel.protbyte.com/api/admin/*),
// so the Cloudflare Access cookie set during /admin login flows on fetch.
// A Worker Route binds the API worker to sentinel.protbyte.com/api/* in
// addition to its api.protbyte.com home, so requests resolve to the same
// worker code. Empty string = same origin as the page.
const ADMIN_BASE = '';

/** React Query cache key holding the active scan result. */
export const CURRENT_SCAN_KEY = ['current-scan'] as const;

// Session persistence so a page refresh / back-button no longer nukes the
// report. sessionStorage (not localStorage) keeps it tab-scoped and cleared on
// tab close — preserving the "nothing stored" promise (nothing leaves the
// browser; no server persistence).
const SCAN_STORAGE_KEY = 'sentinel:scan';

export function persistScan(r: ScanResult): void {
  try { sessionStorage.setItem(SCAN_STORAGE_KEY, JSON.stringify(r)); } catch { /* quota / private mode */ }
}
export function loadPersistedScan(): ScanResult | null {
  try {
    const s = sessionStorage.getItem(SCAN_STORAGE_KEY);
    return s ? (JSON.parse(s) as ScanResult) : null;
  } catch { return null; }
}
export function clearPersistedScan(): void {
  try { sessionStorage.removeItem(SCAN_STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * Start a scan and stream live progress.
 *
 * The endpoint responds with NDJSON: one JSON event per line as each analyzer
 * settles, then a terminal `result` (or `error`) event. `onEvent` receives the
 * `start` and per-module events so the UI can render a real progress console;
 * the promise resolves with the final ScanResult. Pre-flight failures (rate
 * limit, Turnstile, bad domain) still come back as a non-200 JSON body and are
 * translated to thrown errors, exactly as before.
 */
export async function startScan(
  domain: string,
  turnstileToken?: string,
  onEvent?: (e: ScanEvent) => void
): Promise<ScanResult> {
  const r = await fetch(`${BASE}/api/scan`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ domain, turnstile_token: turnstileToken })
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({} as { message?: string; retry_after?: number }));
    if (r.status === 429) {
      const wait = err.retry_after ?? 60;
      throw new Error(`Rate limit reached. Please wait ${wait}s before scanning again.`);
    }
    throw new Error(err.message ?? `Scan failed (${r.status})`);
  }
  if (!r.body) {
    // No stream (e.g. a proxy buffered it into a single JSON body). Fall back.
    const data = (await r.json()) as ScanResult;
    return data;
  }

  const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  let result: ScanResult | null = null;
  let streamError: string | null = null;

  const handleLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let evt: ScanEvent;
    try {
      evt = JSON.parse(trimmed) as ScanEvent;
    } catch {
      return; // ignore a partial / malformed line
    }
    if (evt.type === 'result') result = evt.result;
    else if (evt.type === 'error') streamError = evt.message;
    else onEvent?.(evt);
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      handleLine(buffer.slice(0, nl));
      buffer = buffer.slice(nl + 1);
    }
  }
  handleLine(buffer); // flush any trailing line without a newline

  if (streamError) throw new Error(streamError);
  if (!result) throw new Error('The scan ended before finishing. Please try again.');
  return result;
}

// ---------- Admin dashboard ----------
export interface AdminStats {
  admin_email: string;
  generated_at: number;
  totals: { day: number; week: number; month: number; all_time: number };
  prev: { day: number; week: number; month: number };
  summary: {
    unique_domains: number;
    avg_score: number | null;
    avg_duration_ms: number | null;
    errors: number;
    total: number;
    error_rate: number;
  };
  daily: { d: string; c: number; avg: number | null; errors?: number }[];
  hourly: { dow: number; hour: number; c: number }[];
  score_dist: { bucket: number; c: number }[];
  posture_mix: { posture: string; c: number }[];
  module_avg: { dns: number | null; email: number | null; web: number | null; ssl: number | null; whois?: number | null };
  severity: { critical: number; high: number; medium: number; low: number };
  grade_dist: { grade: string; c: number }[];
  top_domains: { domain: string; c: number; avg: number | null }[];
  by_country: { country: string; c: number }[];
  repeat: { once: number; repeat: number };
  // Observability (Phase D)
  module_dropouts?: { dns: number; email: number; web: number; ssl: number; whois: number; ok_total: number };
  errors_by_category?: { category: string; c: number }[];
  source_health?: { whois_rate_limited: number; total: number };
}

export interface AdminScan {
  ts: number;
  domain: string;
  score: number | null;
  country: string | null;
  status: string | null;
  duration_ms: number | null;
  posture: string | null;
  email_grade: string | null;
  alerts_critical: number | null;
  alerts_high: number | null;
  alerts_medium: number | null;
  alerts_low: number | null;
  exposure_exposed: number | null;
  vuln_count: number | null;
  error_category?: string | null;
  modules_dropped?: string | null;
}

export interface ScanQuery {
  q?: string;
  country?: string;
  status?: 'ok' | 'error' | '';
  min?: number | '';
  max?: number | '';
  from?: number;
  to?: number;
  sort?: 'ts' | 'domain' | 'score' | 'duration';
  dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export async function getAdminStats(): Promise<AdminStats> {
  const r = await fetch(`${ADMIN_BASE}/api/admin/stats`, { credentials: 'include' });
  if (!r.ok) {
    if (r.status === 403) throw new Error('Not signed in. Reload the page to authenticate via Cloudflare Access.');
    throw new Error(`Stats failed (${r.status})`);
  }
  return r.json();
}

export async function getAdminScans(query: ScanQuery = {}): Promise<{ scans: AdminScan[]; total: number }> {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const r = await fetch(`${ADMIN_BASE}/api/admin/scans?${p.toString()}`, { credentials: 'include' });
  if (!r.ok) {
    if (r.status === 403) throw new Error('Not signed in. Reload the page to authenticate via Cloudflare Access.');
    throw new Error(`Scan explorer failed (${r.status})`);
  }
  const data = (await r.json()) as { scans?: AdminScan[]; total?: number };
  return { scans: data.scans ?? [], total: data.total ?? 0 };
}

export interface HealthInfo { ok: boolean; version: string; ts: number }
export async function getHealth(): Promise<HealthInfo> {
  // Public health endpoint — also reachable same-origin via the Worker Route.
  const r = await fetch(`${ADMIN_BASE}/api/health`, { credentials: 'include' });
  if (!r.ok) throw new Error(`Health check failed (${r.status})`);
  return r.json();
}
